import {
  Accordion,
  Anchor,
  Blockquote,
  Button,
  CopyButton,
  Divider,
  Group,
  LoadingOverlay,
  Radio,
  Stack,
  Text,
  TextInput,
  Tooltip,
} from "@mantine/core";
import { modals } from "@mantine/modals";
import {
  IconCheck,
  IconDeviceFloppy,
  IconForms,
  IconListDetails,
  IconSend,
} from "@tabler/icons-react";
import BackButton from "../View/BackButton";
import GrapeFields from "./GrapeFields";
import { useState } from "react";
import CreatableContactListSelector from "../Selectors/CreatableContactListSelector";
import { useLanguage } from "../../stores/LanguageStore";
import { createList } from "../../api/ContactAPI";
import { useFormStore } from "../../stores/FormStore";

// const subdomain_regex = /^[a-zA-Z0-9-]+(\.[a-zA-Z0-9-]+){2,}$/;
const GrapeModal = ({ current }) => {
  const lang = useLanguage((s) => s.language);
  const [items, setItems] = useState(current.model.get("datafields"));
  const [list, setList] = useState(
    current.model.get("contact_list_id")
      ? { id: current.model.get("contact_list_id") }
      : null
  );
  const [listState, setListState] = useState(list ? 2 : 1);
  const [buttonLabel, setButtonLabel] = useState(
    current.model.get("button")?.label
  );
  const [opened, setOpened] = useState("fields");
  const [loading, setLoading] = useState(false);
  const [domainError, setDomainError] = useState(false);

  const handleApply = () => {
    current?.model?.set("datafields", items);
    current?.model?.set("button", { label: buttonLabel });
    current?.model?.set("contact_list_id", list?.id);

    modals.close("grape-modal");
  };

  const handleSave = async (e) => {
    e.preventDefault();
    e.stopPropagation();

    const fd = new FormData(e.target);
    const data = Object.fromEntries(fd);

    current?.model?.set("datafields", items);
    current?.model?.set("button", { label: data.button_label });
    current?.model?.set("name", data.name);

    setDomainError(false);
    // if (!subdomain_regex.test(data.domain)) {
    //   setOpened("settings");
    //   document.getElementById("form-domain").focus();
    //   return setDomainError(true);
    // }
    current?.model?.set("domain", data.domain);

    // Create a list or import to a specific one
    if (data.contact_list_name) {
      setLoading(true);
      createList({ name: data.contact_list_name })
        .then((response) => {
          current?.model?.set("contact_list_id", response.data.contact_list_id);
          modals.close("grape-modal");

          useFormStore.getState().setToSave({
            name: data.name,
            domain: data.domain,
            contact_list_id: response.data.contact_list_id,
          });
        })
        .finally(() => setLoading(false));
    } else {
      current?.model?.set("contact_list_id", list?.id);
      modals.close("grape-modal");
      useFormStore.getState().setToSave({
        name: data.name,
        domain: data.domain,
        contact_list_id: list?.id,
      });
    }
  };

  return (
    <Stack component={"form"} onSubmit={handleSave}>
      <LoadingOverlay visible={loading} />

      <Accordion value={opened} onChange={setOpened} variant="separated">
        <Accordion.Item key={"fields"} value={"fields"}>
          <Accordion.Control icon={<IconForms size={18} />}>
            {lang.forms.fields}
          </Accordion.Control>
          <Accordion.Panel>
            <TextInput
              label={lang.forms.send_label}
              value={buttonLabel}
              onChange={(e) => setButtonLabel(e.target.value)}
              name="button_label"
              size="md"
              required
              mb={16}
            />
            <GrapeFields items={items} setItems={setItems} />
          </Accordion.Panel>
        </Accordion.Item>

        <Accordion.Item key={"settings"} value={"settings"}>
          <Accordion.Control icon={<IconSend size={18} />}>
            {lang.forms.base_config}
          </Accordion.Control>
          <Accordion.Panel>
            <Stack>
              <TextInput
                label={lang.forms.form_name}
                defaultValue={current.model.get("name")}
                name="name"
                size="md"
                required
              />
              <Divider opacity={0.5} />

              <Blockquote
                radius={"md"}
                color="blue"
                styles={{
                  root: {
                    padding: 12,
                  },
                }}
                component={Group}
              >
                <Text>
                  {lang.forms.domain_text}
                  <CopyButton value={"resellers-pool1.magileads.net"}>
                    {({ copied, copy }) => (
                      <Tooltip
                        label={copied ? lang.global.copied : lang.global.copy}
                        color={copied ? "green" : "black"}
                      >
                        <Anchor
                          c={"black"}
                          onClick={copy}
                          truncate="end"
                          fw={600}
                          ml={12}
                        >
                          {"resellers-pool1.magileads.net"}
                        </Anchor>
                      </Tooltip>
                    )}
                  </CopyButton>
                </Text>
              </Blockquote>

              <TextInput
                label={lang.forms.sub_domain}
                defaultValue={current.model.get("domain")}
                placeholder="sub.domain.ext"
                name="domain"
                size="md"
                id="form-domain"
                error={domainError ? lang.forms.invalid_domain : false}
              />

              <Divider opacity={0.5} />

              <Text c="dimmed" size="sm">
                {lang.forms.list_text}
              </Text>
              <Group wrap="nowrap" align="end" gap={"lg"}>
                <Stack w="100%" gap={8}>
                  <Radio
                    label={lang.targeting_linkedin_google.create_new_list}
                    name="list_state"
                    checked={listState === 1}
                    size="sm"
                    onChange={() => setListState(1)}
                  />
                  <TextInput
                    id="new-list-name"
                    name="contact_list_name"
                    w={"100%"}
                    disabled={listState !== 1}
                    styles={{ label: { fontSize: 13 } }}
                    label={lang.targeting_linkedin_google.list_name}
                    placeholder={lang.targeting_linkedin_google.list_name}
                    // required={listState === 1}
                  />
                </Stack>
                <Stack w="100%" gap={8}>
                  <Radio
                    label={lang.targeting_linkedin_google.import}
                    name="list_state"
                    size="sm"
                    checked={listState === 2}
                    onChange={() => setListState(2)}
                  />
                  <CreatableContactListSelector
                    value={list}
                    setValue={setList}
                    label={lang.targeting_linkedin_google.select_list}
                    placeholder={lang.contact_lists.create.dialog.desc}
                    w={"100%"}
                    disabled={listState !== 2}
                    styles={{ label: { fontSize: 13 } }}
                    // required={listState === 2}
                    leftSection={<IconListDetails size={16} />}
                    disableCreate
                    noDesc
                  />
                </Stack>
              </Group>
            </Stack>
          </Accordion.Panel>
        </Accordion.Item>
      </Accordion>

      <Group
        justify="end"
        pos={"sticky"}
        bottom={0}
        py={12}
        bg={"#fff"}
        style={{ zIndex: 10 }}
      >
        <BackButton onClick={() => modals.close("grape-modal")} />

        <Button
          leftSection={<IconCheck size={18} />}
          variant="light"
          size="md"
          ml={"auto"}
          onClick={handleApply}
        >
          {lang.forms.apply_fields}
        </Button>

        <Button
          leftSection={<IconDeviceFloppy size={20} />}
          size="md"
          type="submit"
          onClick={() => setOpened("settings")}
          loading={loading}
        >
          {lang.forms.save_form}
        </Button>
      </Group>
    </Stack>
  );
};

export default GrapeModal;
